import { DocumentSplit, STDocument } from '@st/sdk'

/**
 * Builds a **new** array of `DocumentSplit` objects that reflects how the
 * document *would* look if the user inserted ("add") or removed ("remove") a
 * break immediately **after** the provided page.
 *
 * The function is pure and never mutates `doc`.
 * Pass its return value to a server API call to persist the change.
 *
 * Behaviour summary
 * • `action == "add"`   – Splits the range that spans `breakAfterPageNumber`
 *   into two smaller ranges and marks both as `"uncategorized"` because the
 *   original category can no longer be assumed.
 * • `action == "remove"` – Merges the two contiguous ranges that are split at the page break.
 *
 * @example
 * ```typescript
 * const nextState = updatedDocumentSplits(doc, 'add', 3)
 * // doc remains unchanged; send `nextState` to the API.
 * ```
 *
 * @param doc - Source document whose existing `splits` are examined.
 * @param action - "add" to insert a break, "remove" to undo one.
 * @param breakAfterPageNumber - 1-based page number **after** which the page break is applied.
 * @returns A brand-new array representing the desired post-operation state.
 * @throws {Error} If an unsupported `action` is provided.
 *
 * @see STDocument#splits
 */
export function updatedDocumentSplits(
  doc: STDocument,
  action: 'add' | 'remove',
  breakAfterPageNumber: number
): DocumentSplit[] {
  // For document split, ranges are inclusive
  // For example, 2..5 means 2, 3, 4, 5
  //
  // If we were to split after a page 3, that would result in
  // the split getting divided into 2..3 and 4..5
  //
  // If we were to delete that split, it would get merged back into 2..5
  const rangeBeforePageBreakLast = breakAfterPageNumber // last page of the "previous" range
  const rangeAfterPageBreakFirst = breakAfterPageNumber + 1 // first page of the "next" range

  if (action == 'add') {
    const splitsAfterAdd: DocumentSplit[] = []
    doc.splits.forEach((split) => {
      // Does this split straddle the break point? If so, divide it.
      if (
        rangeContains(split.pageRange, rangeBeforePageBreakLast) &&
        rangeContains(split.pageRange, rangeAfterPageBreakFirst)
      ) {
        // Left portion (before the break)
        splitsAfterAdd.push({
          pageRange: { first: split.pageRange.first, last: rangeBeforePageBreakLast },
          inferredDocumentTypeId: 'uncategorized'
        })
        // Right portion (after the break)
        splitsAfterAdd.push({
          pageRange: { first: rangeAfterPageBreakFirst, last: split.pageRange.last },
          inferredDocumentTypeId: 'uncategorized'
        })
      } else {
        // This split is unaffected; carry it forward as-is.
        splitsAfterAdd.push(split)
      }
    })
    return splitsAfterAdd
  } else if (action == 'remove') {
    const splitsAfterRemove: DocumentSplit[] = []
    const splitsToRemove = new Set<DocumentSplit>()
    doc.splits.forEach((split, index) => {
      const nextSplit = doc.splits[index + 1]

      // If we delete the page break that THIS split ends with, we are effectively
      // merging it with the next contiguous split
      if (split.pageRange.last == rangeBeforePageBreakLast && nextSplit) {
        // We are at the split immediately **before** the break; extend it to
        // swallow the next range.
        splitsAfterRemove.push({
          pageRange: { first: split.pageRange.first, last: nextSplit.pageRange.last },
          inferredDocumentTypeId: 'uncategorized'
        })
        splitsToRemove.add(nextSplit)
      } else if (splitsToRemove.has(split)) {
        // This split has already been removed; skip it.
      } else {
        // Unaffected split.
        splitsAfterRemove.push(split)
      }
    })
    return splitsAfterRemove
  }

  throw new Error('Invalid action')

  function rangeContains(range: DocumentSplit['pageRange'], pageNumber: number) {
    // Inclusive containment check: first ≤ pageNumber ≤ last
    return range.first <= pageNumber && pageNumber <= range.last
  }
}
